import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, Transition as _Transition, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "page-wrapper"
}
const _hoisted_2 = {
  key: 3,
  class: "page-wrapper",
  id: "resource-detail-main-content",
  tabindex: "0"
}
const _hoisted_3 = { class: "content-frame-wrapper" }
const _hoisted_4 = { class: "content-wrapper" }
const _hoisted_5 = {
  key: 0,
  class: "completion-badge-wrapper"
}
const _hoisted_6 = {
  key: 0,
  class: "title-wrapper",
  "data-test": "title-wrapper"
}
const _hoisted_7 = {
  key: 1,
  class: "badge-wrapper"
}
const _hoisted_8 = { id: "timer-text" }
const _hoisted_9 = {
  class: "page-content",
  "data-test": "page-content"
}
const _hoisted_10 = {
  key: 0,
  class: "completion-button-wrapper"
}
const _hoisted_11 = {
  key: 1,
  class: "button-wrapper"
}

import { computed, ref, onMounted } from "vue";
import { IonPage, IonContent, IonBadge, IonIcon, onIonViewDidEnter } from "@ionic/vue";
import MobileHeader from "@/components/Common/MobileHeader/MobileHeader.vue";
import { SanityBlocks } from "sanity-blocks-vue-component";
import VideoSerializer from "@/components/Resource/Serializers/VideoSerializer/VideoSerializer.vue";
import StorylineSerializer from "@/components/Resource/Serializers/StorylineSerializer/StorylineSerializer.vue";
import BookwidgetSerializer from "@/components/Resource/Serializers/BookwidgetSerializer/BookwidgetSerializer.vue";
import SectionReference from "@/components/Serializers/SectionReference.vue";
import ImageSerializer from "@/components/Serializers/ImageSerializer.vue";
import LinkSerializer from "@/components/Serializers/LinkSerializer.vue";
import {
  chevronForwardOutline,
  chevronBackOutline,
  timeOutline,
  checkmarkCircleOutline,
} from "ionicons/icons";
import {
  useResourceStore,
  useErrorStore,
  useLocaleStore,
  useAssessmentStore,
  RecommendationType,
  useCourseStore,
} from "@/store";
import PageBanner from "@/components/Common/PageBanner/PageBanner.vue";
import Breadcrumbs from "@/components/Common/Breadcrumbs/Breadcrumbs.vue";
import ResourceDetailSkeleton from "@/components/Skeletons/ResourceDetailSkeleton/ResourceDetailSkeleton.vue";
import { parseImageUrl, mapResourceType } from "@/helpers";
import DesktopLocaleSelector from "@/components/Common/LocaleSelector/DesktopLocaleSelector.vue";
import { createLocaleSpecificSanityId } from "@/helpers/CreateLocaleSpecificSanityId.js";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import { reportCourseProgress } from "@/helpers/reporting";
import { onBeforeRouteUpdate, useRoute, useRouter } from "vue-router";

const incompleteText = "resource.activityInComplete";
const completedText = "resource.activityCompleted";

export default /*@__PURE__*/_defineComponent({
  __name: 'ResourceDetail',
  props: {
  id: { type: String, required: true },
},
  setup(__props) {

const resourceStore = useResourceStore();
const courseStore = useCourseStore();
const localeStore = useLocaleStore();
const assessmentStore = useAssessmentStore();
const route = useRoute();
const router = useRouter();

const props = __props;

onIonViewDidEnter(() => {
  const initialId = (route.params.id as string) ?? props.id;
  if (initialId) void loadResource(initialId);
});

onBeforeRouteUpdate((to) => {
  const id = to.params.id as string;
  if (id) void loadResource(id);
});

const completeHasBeenToggled = ref(false);
const serializers = {
  types: {
    video_reference: VideoSerializer,
    section_reference: SectionReference,
    storyline_reference: StorylineSerializer,
    bookwidget_reference: BookwidgetSerializer,
    image: ImageSerializer,
  },
  styles: {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    h7: "h7",
    h8: "h8",
    normal: "p",
  },
  marks: {
    link: LinkSerializer,
  }
};

const doc = computed(() => {
  return resourceStore.getResource;
});

const grandparent = computed(() => {
  return doc.value.parent?.grandparent;
});

const badges = computed(() => {
  return doc.value.resource_type || doc.value.time;
});

const bannerImage = computed(() => {
  return doc.value.feature_image
    ? (parseImageUrl(doc.value.feature_image, 1000, 2500) as string)
    : "https://cdn.sanity.io/images/2i80ck01/~production/8135cdce59aec36c855d65c473f944f115bca081-4032x3024.jpg?auto=format&fit=max&w=500";
});
const isTypeCourse = computed(() => {
  return doc.value.parent?._type === "course";
});

const topics = computed(() => doc.value?.resourceSiblings?.[0]?.topics ?? []);

const goPrev = async () => {
  const id = prevResource.value;
  if (!id) return;
  await router.replace({ name: 'ResourceDetail', params: { id } });
  window.location.reload();
}

const goNext = async () => {
  const id = nextResource.value
  if (!id) return
  await router.replace({ name: 'ResourceDetail', params: { id } })
  window.location.reload();
}

const currentResourceIndex = computed(() => {
  const activeId = (route.params.id as string) || props.id;
  if (!activeId) return -1
    return topics.value.findIndex((t: { base_id: string; }) => t?.base_id === activeId);
});

const nextResource = computed(() => {
  const i = currentResourceIndex.value;
  return (i >= 0 && i < topics.value.length - 1) ? (topics.value[i + 1]?.base_id ?? '') : '';
});

const prevResource = computed(() => {
  const i = currentResourceIndex.value;
  return (i > 0) ? (topics.value[i - 1]?.base_id ?? '') : '';
});

const isResourceCompleted = computed(() => {
  return resourceStore.getResource.completed;
});

const completedButtonText = computed(() => {
  return isResourceCompleted.value ? completedText : incompleteText;
});

const assessmentRecommendedResource = computed(() => {
  return assessmentStore.getAssessmentRecommendations.find(
    (recommendation) =>
      recommendation.type === RecommendationType.Resource &&
      recommendation.content.base_id === doc.value.base_id
  );
});

onMounted(async (): Promise<void> => {
  loadResource(props.id);  
});

const toggleCompletion = (): void => {
  resourceStore.resource.document.completed = !isResourceCompleted.value;
  completeHasBeenToggled.value = true;
  resourceStore.markResourceComplete(isResourceCompleted.value);
  resourceStore.updateResourceCompletion(isResourceCompleted.value);
  
  if(resourceStore.resource.document.parent._type == 'course')
    reportCourseProgress(courseStore.getCourseDetail.base_id, courseStore.getCourseDetail.completedResourceCount, courseStore.getCourseDetail.totalResourceCount);

  if (assessmentRecommendedResource.value) {
    assessmentStore.markAssessmentRecommendedResourceComplete(
      doc.value.base_id,
      isResourceCompleted.value
    );
  }
};

async function loadResource(id: string) {
  //abstract mounted logic, to allow retrigger on route change
  try {
    const sanityId = createLocaleSpecificSanityId(id);
    await resourceStore.requestResource(sanityId);

    if(courseStore.getCourseDetail._id == null && resourceStore.resource.document.parent._type == 'course')
      courseStore.requestCourseDetail(resourceStore.resource.document.parent._id);
  } catch (error: any) {
    if (error.response && error.response.status === 403) {
      if (localeStore.getCurrentLocaleCode.includes("en")) {
        await resourceStore.requestResource(props.id as string);
      } else {
        resourceStore.$reset();
        const errorStore = useErrorStore();
        errorStore.setGenericError(true);
      }
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonPage), null, {
    default: _withCtx(() => [
      _createVNode(MobileHeader),
      _createVNode(_unref(IonContent), {
        "data-test": "learn-detail",
        class: "page-wrapper"
      }, {
        default: _withCtx(() => [
          _createVNode(DesktopLocaleSelector),
          (!_unref(resourceStore).isResourceLoaded)
            ? (_openBlock(), _createBlock(PageBanner, {
                key: 0,
                isImageLoading: true
              }))
            : (_openBlock(), _createBlock(PageBanner, {
                key: 1,
                imagePath: bannerImage.value
              }, null, 8, ["imagePath"])),
          (!_unref(resourceStore).isResourceLoaded)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(ResourceDetailSkeleton)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                _createVNode(Breadcrumbs, {
                  tab: "learn-home",
                  parentType: doc.value?.parent?._type ?? '',
                  grandparentType: grandparent.value?._type,
                  parent: doc.value?.parent?.base_id ?? '',
                  parentTitle: doc.value?.parent?.title ?? '',
                  grandparent: grandparent.value?.base_id,
                  grandparentTitle: grandparent.value?.title,
                  doc: doc.value?.title
                }, null, 8, ["parentType", "grandparentType", "parent", "parentTitle", "grandparent", "grandparentTitle", "doc"]),
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_Transition, { name: "slide-fade" }, {
                      default: _withCtx(() => [
                        (isResourceCompleted.value && !completeHasBeenToggled.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                              _createVNode(_unref(IonBadge), {
                                color: "primary",
                                id: "completion-badge",
                                "data-test": "completion-badge"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(IonIcon), {
                                    icon: _unref(checkmarkCircleOutline),
                                    "aria-hidden": "true"
                                  }, null, 8, ["icon"]),
                                  _createTextVNode(" " + _toDisplayString(_ctx.$t("resource.activityCompleted")), 1)
                                ]),
                                _: 1
                              })
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    }),
                    (doc.value?.title)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                          _createElementVNode("h4", null, _toDisplayString(doc.value?.title), 1)
                        ]))
                      : _createCommentVNode("", true),
                    (badges.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          (_unref(mapResourceType)(doc.value?.resource_type))
                            ? (_openBlock(), _createBlock(_unref(IonBadge), {
                                key: 0,
                                color: "secondary"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t(_unref(mapResourceType)(doc.value?.resource_type))), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (doc.value?.time)
                            ? (_openBlock(), _createBlock(_unref(IonBadge), {
                                key: 1,
                                id: "timer"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(IonIcon), {
                                    icon: _unref(timeOutline),
                                    "aria-hidden": "true"
                                  }, null, 8, ["icon"]),
                                  _createElementVNode("p", _hoisted_8, _toDisplayString(doc.value?.time + " " + _ctx.$t("resource.readingTimeMins")), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_9, [
                      _createVNode(_unref(SanityBlocks), {
                        blocks: doc.value?.body,
                        serializers: serializers
                      }, null, 8, ["blocks"]),
                      (doc.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createVNode(AppButton, {
                              size: "default",
                              type: isResourceCompleted.value ? 'primary' : 'tertiary',
                              width: "312px",
                              label: _ctx.$t(completedButtonText.value),
                              ariaLabel: _ctx.$t(completedButtonText.value),
                              icon: _unref(checkmarkCircleOutline),
                              iconPosition: "start",
                              onClick: toggleCompletion,
                              "data-test": "complete-button"
                            }, null, 8, ["type", "label", "ariaLabel", "icon"])
                          ]))
                        : _createCommentVNode("", true),
                      (isTypeCourse.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                            (prevResource.value)
                              ? (_openBlock(), _createBlock(AppButton, {
                                  key: 0,
                                  size: "default",
                                  type: "tertiary",
                                  label: _ctx.$t('resource.activity.previous'),
                                  ariaLabel: _ctx.$t('resource.activity.previous'),
                                  icon: _unref(chevronBackOutline),
                                  iconPosition: "start",
                                  onClick: goPrev,
                                  "data-test": "previous-activity"
                                }, null, 8, ["label", "ariaLabel", "icon"]))
                              : _createCommentVNode("", true),
                            (nextResource.value)
                              ? (_openBlock(), _createBlock(AppButton, {
                                  key: 1,
                                  size: "default",
                                  type: "primary",
                                  label: _ctx.$t('resource.activity.next'),
                                  ariaLabel: _ctx.$t('resource.activity.next'),
                                  icon: _unref(chevronForwardOutline),
                                  iconPosition: "end",
                                  onClick: goNext,
                                  class: "next-button",
                                  "data-test": "next-activity"
                                }, null, 8, ["label", "ariaLabel", "icon"]))
                              : _createCommentVNode("", true)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ])
              ]))
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})